/**
 * Error Log Viewer Component
 * Allows viewing and exporting error logs
 */

import React, { useState, useEffect } from "react";
import {
  getErrorLogs,
  clearErrorLogs,
  exportErrorLogs,
  exportErrorLogsAsText,
  getErrorLogStats,
  getErrorLogStatsFromLogs,
  fetchErrorLogsFromGoogleSheet,
  ErrorLogEntry,
} from "utils/errorLogger";
import { generateEmailBodyForUI } from "utils/errorHandler";

const ErrorLogViewer: React.FC = () => {
  const [logs, setLogs] = useState<ErrorLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ErrorLogEntry[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [stats, setStats] = useState(getErrorLogStats());
  const [expandedEmailRows, setExpandedEmailRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, selectedModule, searchQuery]);

  const loadLogs = () => {
    const allLogs = getErrorLogs();
    setLogs(allLogs);
    setStats(getErrorLogStats());
  };

  const loadSheetLogs = async () => {
    try {
      const sheetLogs = await fetchErrorLogsFromGoogleSheet(500);
      setLogs(sheetLogs);
      setStats(getErrorLogStatsFromLogs(sheetLogs));
    } catch (e) {
      alert("Failed to load logs from Google Sheet");
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    // Filter by module
    if (selectedModule !== "all") {
      filtered = filtered.filter((log) => log.module === selectedModule);
    }

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (log) =>
            log.error.message.toLowerCase().includes(query) ||
            log.action.toLowerCase().includes(query) ||
            log.module.toLowerCase().includes(query) ||
            (log.context.url && log.context.url.toLowerCase().includes(query)) ||
            (log.context.fileName && log.context.fileName.toLowerCase().includes(query)) ||
            (log.context.licenseType && log.context.licenseType.toLowerCase().includes(query)) ||
            (log.context.userName && log.context.userName.toLowerCase().includes(query)) ||
            (log.context.requestBody && JSON.stringify(log.context.requestBody).toLowerCase().includes(query))
        );
      }

    setFilteredLogs(filtered);
  };

  const handleClear = () => {
    if (window.confirm("Are you sure you want to clear all error logs? This cannot be undone.")) {
      clearErrorLogs();
      loadLogs();
    }
  };

  const modules = Array.from(new Set(logs.map((log) => log.module)));

  const formatDate = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="container-fluid" style={{ padding: "20px" }}>
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center" style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          border: "none"
        }}>
          <h4 className="mb-0" style={{ color: "white", fontWeight: "600" }}>
            <i className="ri-file-warning-line me-2" />
            Error Logs Viewer
          </h4>
          <div className="d-flex gap-2">
            <button className="btn btn-sm text-white" style={{ backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }} onClick={exportErrorLogs}>
              <i className="ri-download-line me-1" />
              Export JSON
            </button>
            <button className="btn btn-sm text-white" style={{ backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }} onClick={exportErrorLogsAsText}>
              <i className="ri-file-text-line me-1" />
              Export Text
            </button>
            <button className="btn btn-sm text-white" style={{ backgroundColor: "rgba(220,53,69,0.8)", border: "none" }} onClick={handleClear}>
              <i className="ri-delete-bin-line me-1" />
              Clear All
            </button>
            <button className="btn btn-sm text-white" style={{ backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }} onClick={loadLogs}>
              <i className="ri-refresh-line me-1" />
              Refresh
            </button>
            <button
              className="btn btn-sm text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}
              onClick={loadSheetLogs}
            >
              <i className="ri-google-line me-1" />
              Load Sheet
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Statistics */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card text-white" style={{ 
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
              }}>
                <div className="card-body">
                  <h6 className="card-title" style={{ opacity: 0.9, fontSize: "13px", fontWeight: "500" }}>
                    <i className="ri-error-warning-line me-1" />
                    Total Errors
                  </h6>
                  <h3 className="mb-0" style={{ fontWeight: "700", fontSize: "28px" }}>{stats.total}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-white" style={{ 
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                border: "none",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
              }}>
                <div className="card-body">
                  <h6 className="card-title" style={{ opacity: 0.9, fontSize: "13px", fontWeight: "500" }}>
                    <i className="ri-time-line me-1" />
                    Last 24 Hours
                  </h6>
                  <h3 className="mb-0" style={{ fontWeight: "700", fontSize: "28px" }}>{stats.recent}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-white" style={{ 
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                border: "none",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
              }}>
                <div className="card-body">
                  <h6 className="card-title" style={{ opacity: 0.9, fontSize: "13px", fontWeight: "500" }}>
                    <i className="ri-stack-line me-1" />
                    Modules
                  </h6>
                  <h3 className="mb-0" style={{ fontWeight: "700", fontSize: "28px" }}>{Object.keys(stats.byModule).length}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-white" style={{ 
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                border: "none",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
              }}>
                <div className="card-body">
                  <h6 className="card-title" style={{ opacity: 0.9, fontSize: "13px", fontWeight: "500" }}>
                    <i className="ri-filter-line me-1" />
                    Filtered Results
                  </h6>
                  <h3 className="mb-0" style={{ fontWeight: "700", fontSize: "28px" }}>{filteredLogs.length}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Filter by Module</label>
              <select
                className="form-select"
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
              >
                <option value="all">All Modules</option>
                {modules.map((module) => (
                  <option key={module} value={module}>
                    {module} ({stats.byModule[module] || 0})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by error message, action, URL, file name, license type, user name, request body..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Error Logs Table */}
          <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto", borderRadius: "8px", border: "1px solid #e9ecef" }}>
            <table className="table table-hover mb-0">
              <thead style={{ 
                position: "sticky", 
                top: 0, 
                zIndex: 1,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white"
              }}>
                <tr>
                  <th style={{ width: "150px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>Timestamp</th>
                  <th style={{ width: "120px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>Module</th>
                  <th style={{ width: "150px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>Action</th>
                  <th style={{ width: "200px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>File:Line</th>
                  <th style={{ color: "white", fontWeight: "600", border: "none", padding: "12px" }}>Error Message</th>
                  <th style={{ width: "100px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>User ID</th>
                  <th style={{ width: "120px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>License Type</th>
                  <th style={{ width: "120px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>User Name</th>
                  <th style={{ width: "80px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>Method</th>
                  <th style={{ width: "150px", color: "white", fontWeight: "600", border: "none", padding: "12px" }}>Request Body</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4">
                      No error logs found
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log, index) => {
                    const emailData = generateEmailBodyForUI(
                      { message: log.error.message, stack: log.error.stack },
                      {
                        module: log.module as any,
                        action: log.action,
                        userId: log.context.userId,
                        userEmail: log.context.userEmail,
                        licenseType: log.context.licenseType,
                        userName: log.context.userName,
                        url: log.context.url,
                        method: log.context.method,
                        requestBody: log.context.requestBody,
                        additionalInfo: log.context.additionalInfo,
                        fileName: log.context.fileName,
                        lineNumber: log.context.lineNumber,
                      },
                      log.timestamp
                    );
                    const isEmailExpanded = expandedEmailRows.has(index);
                    
                    return (
                      <React.Fragment key={index}>
                    <tr style={{ 
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e7f3ff"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8f9fa"}
                    >
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <small style={{ color: "#6c757d", fontWeight: "500" }}>{formatDate(log.timestamp)}</small>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <span className="badge" style={{ 
                          backgroundColor: "#6366f1", 
                          color: "white",
                          fontWeight: "600",
                          padding: "6px 12px",
                          fontSize: "12px",
                          borderRadius: "6px",
                          boxShadow: "0 2px 4px rgba(99, 102, 241, 0.3)"
                        }}>
                          {log.module}
                        </span>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <span className="badge" style={{
                          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                          color: "white",
                          fontWeight: "700",
                          padding: "7px 14px",
                          fontSize: "11px",
                          fontFamily: "monospace",
                          textTransform: "none",
                          borderRadius: "6px",
                          boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                          letterSpacing: "0.3px"
                        }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        {log.context.fileName && !log.context.fileName.includes('bundle') && !log.context.fileName.includes('chunk') ? (
                          <div style={{ fontFamily: "monospace", fontSize: "11px" }}>
                            <div 
                              className="text-truncate" 
                              title={`${log.context.fileName}${log.context.lineNumber ? `:${log.context.lineNumber}` : ''}`}
                              style={{ 
                                fontWeight: "700", 
                                color: "#059669", 
                                cursor: "pointer",
                                padding: "4px 8px",
                                backgroundColor: "#d1fae5",
                                borderRadius: "4px",
                                display: "inline-block",
                                transition: "all 0.2s"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#a7f3d0";
                                e.currentTarget.style.transform = "scale(1.05)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#d1fae5";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                              onClick={() => {
                                // Copy file name and line to clipboard
                                const text = `${log.context.fileName}${log.context.lineNumber ? `:${log.context.lineNumber}` : ''}`;
                                navigator.clipboard.writeText(text);
                                alert(`Copied: ${text}`);
                              }}
                            >
                              {log.context.fileName}
                            </div>
                            {log.context.lineNumber && (
                              <div style={{ 
                                fontSize: "10px", 
                                marginTop: "4px", 
                                fontWeight: "600",
                                color: "#047857"
                              }}>
                                Line {log.context.lineNumber}
                              </div>
                            )}
                          </div>
                        ) : log.context.fileName ? (
                          <div style={{ fontFamily: "monospace", fontSize: "11px" }}>
                            <div className="text-truncate" title="Source file not found - showing bundle.js reference" style={{
                              padding: "4px 8px",
                              backgroundColor: "#fef3c7",
                              borderRadius: "4px",
                              display: "inline-block"
                            }}>
                              <small style={{ color: "#92400e" }}>⚠️ {log.context.fileName}</small>
                            </div>
                            {log.context.lineNumber && (
                              <div style={{ fontSize: "10px", marginTop: "4px", color: "#92400e" }}>
                                Line {log.context.lineNumber} (bundle)
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af" }} title="File name not available - check stack trace for details">-</span>
                        )}
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <div style={{ maxWidth: "400px" }}>
                          <div className="text-truncate" title={log.error.message} style={{
                            color: "#dc2626",
                            fontWeight: "500",
                            fontSize: "13px"
                          }}>
                            {log.error.message}
                          </div>
                          {log.context.requestBody && (
                            <details className="mt-2">
                              <summary style={{ 
                                cursor: "pointer", 
                                fontSize: "11px",
                                color: "#10b981",
                                fontWeight: "500",
                                userSelect: "none"
                              }}>
                                <i className="ri-arrow-down-s-line" /> View Request Payload
                              </summary>
                              <pre
                                style={{
                                  fontSize: "10px",
                                  maxHeight: "200px",
                                  overflow: "auto",
                                  backgroundColor: "#064e3b",
                                  color: "#d1fae5",
                                  padding: "12px",
                                  marginTop: "8px",
                                  borderRadius: "6px",
                                  border: "1px solid #047857",
                                  fontFamily: "monospace",
                                  lineHeight: "1.5"
                                }}
                              >
                                {JSON.stringify(log.context.requestBody, null, 2)}
                              </pre>
                            </details>
                          )}
                          {log.error.stack && (
                            <details className="mt-2">
                              <summary style={{ 
                                cursor: "pointer", 
                                fontSize: "11px",
                                color: "#6366f1",
                                fontWeight: "500",
                                userSelect: "none"
                              }}>
                                <i className="ri-arrow-down-s-line" /> View Stack Trace
                              </summary>
                              <pre
                                style={{
                                  fontSize: "10px",
                                  maxHeight: "150px",
                                  overflow: "auto",
                                  backgroundColor: "#1f2937",
                                  color: "#f3f4f6",
                                  padding: "12px",
                                  marginTop: "8px",
                                  borderRadius: "6px",
                                  border: "1px solid #374151",
                                  fontFamily: "monospace",
                                  lineHeight: "1.5"
                                }}
                              >
                                {log.error.stack}
                              </pre>
                            </details>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <small style={{ color: "#6b7280", fontFamily: "monospace" }}>{log.context.userId || "-"}</small>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        {log.context.licenseType ? (
                          <span className="badge" style={{
                            backgroundColor: "#8b5cf6",
                            color: "white",
                            fontWeight: "600",
                            padding: "5px 10px",
                            fontSize: "11px",
                            borderRadius: "6px"
                          }}>
                            {log.context.licenseType}
                          </span>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <small style={{ color: "#6b7280", fontWeight: "500" }}>{log.context.userName || "-"}</small>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        <span className="badge" style={{
                          backgroundColor: "#0ea5e9",
                          color: "white",
                          fontWeight: "600",
                          padding: "5px 10px",
                          fontSize: "11px",
                          borderRadius: "6px"
                        }}>
                          {log.context.method || "GET"}
                        </span>
                      </td>
                      <td style={{ padding: "12px", verticalAlign: "middle" }}>
                        {log.context.requestBody ? (
                          <details className="mt-1">
                            <summary className="text-muted" style={{ cursor: "pointer", fontSize: "11px" }}>
                              View Payload
                            </summary>
                            <pre
                              style={{
                                fontSize: "10px",
                                maxHeight: "100px",
                                overflow: "auto",
                                backgroundColor: "#f0f0f0",
                                color: "#333",
                                padding: "8px",
                                marginTop: "4px",
                                borderRadius: "4px",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                              }}
                            >
                              {JSON.stringify(log.context.requestBody, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                    {/* Email Content Row */}
                    <tr>
                      <td colSpan={10} style={{ padding: 0, border: "none" }}>
                        <div style={{ 
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #dc3545",
                          margin: "0 0 10px 0"
                        }}>
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedEmailRows);
                              if (isEmailExpanded) {
                                newExpanded.delete(index);
                              } else {
                                newExpanded.add(index);
                              }
                              setExpandedEmailRows(newExpanded);
                            }}
                            style={{
                              width: "100%",
                              padding: "12px 20px",
                              backgroundColor: isEmailExpanded ? "#dc3545" : "#e9ecef",
                              color: isEmailExpanded ? "white" : "#495057",
                              border: "none",
                              cursor: "pointer",
                              textAlign: "left",
                              fontWeight: "600",
                              fontSize: "14px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              transition: "all 0.3s"
                            }}
                          >
                            <span>
                              <i className="ri-mail-line me-2" />
                              {isEmailExpanded ? "Hide" : "Show"} Email Notification Content
                            </span>
                            <i className={`ri-arrow-${isEmailExpanded ? "up" : "down"}-s-line`} />
                          </button>
                          {isEmailExpanded && (
                            <div style={{ padding: "20px", backgroundColor: "white" }}>
                              {/* Email Header */}
                              <div style={{
                                background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                                color: "white",
                                padding: "20px",
                                borderRadius: "8px 8px 0 0",
                                marginBottom: "0"
                              }}>
                                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700" }}>
                                  ⚠️ API Error Alert
                                </h3>
                                <p style={{ margin: "10px 0 0 0", fontSize: "14px", opacity: 0.95 }}>
                                  An error occurred in {emailData.moduleName} module
                                </p>
                              </div>

                              {/* Error Details Table */}
                              <div style={{
                                border: "1px solid #ddd",
                                borderTop: "none",
                                borderRadius: "0 0 8px 8px",
                                padding: "20px",
                                backgroundColor: "white"
                              }}>
                                <h4 style={{ color: "#dc3545", marginTop: 0, marginBottom: "15px", fontSize: "16px" }}>
                                  Error Details
                                </h4>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                  <tbody>
                                    {emailData.errorDetails.map((detail: { label: string; value: string }, idx: number) => (
                                      <tr key={idx} style={{ borderBottom: "1px solid #e9ecef" }}>
                                        <td style={{
                                          padding: "10px 15px",
                                          backgroundColor: "#f8f9fa",
                                          fontWeight: "600",
                                          width: "200px",
                                          borderRight: "1px solid #e9ecef",
                                          fontSize: "13px",
                                          color: "#495057"
                                        }}>
                                          {detail.label}
                                        </td>
                                        <td style={{
                                          padding: "10px 15px",
                                          fontSize: "13px",
                                          color: detail.label === "Error Message" ? "#dc3545" : "#212529",
                                          fontFamily: detail.label === "API URL" || detail.label === "File Name" ? "monospace" : "inherit",
                                          wordBreak: "break-word"
                                        }}>
                                          {detail.value}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {/* Request Body Section - Dark Green Background */}
                                {emailData.requestBody && (
                                  <div style={{ marginTop: "25px" }}>
                                    <h4 style={{ color: "#6c757d", marginBottom: "10px", fontSize: "14px", fontWeight: "600" }}>
                                      Request Body
                                    </h4>
                                    <pre style={{
                                      background: "#064e3b",
                                      color: "#d1fae5",
                                      padding: "15px",
                                      borderRadius: "6px",
                                      overflowX: "auto",
                                      overflowY: "auto",
                                      fontSize: "11px",
                                      maxHeight: "400px",
                                      border: "1px solid #047857",
                                      margin: 0,
                                      fontFamily: "monospace",
                                      lineHeight: "1.6"
                                    }}>
                                      {emailData.requestBody}
                                    </pre>
                                  </div>
                                )}

                                {/* Stack Trace Section */}
                                {emailData.stackTrace && (
                                  <div style={{ marginTop: "25px" }}>
                                    <h4 style={{ color: "#6c757d", marginBottom: "10px", fontSize: "14px", fontWeight: "600" }}>
                                      Stack Trace
                                    </h4>
                                    <pre style={{
                                      background: "#2d2d2d",
                                      color: "#f8f8f2",
                                      padding: "15px",
                                      borderRadius: "6px",
                                      overflowX: "auto",
                                      fontSize: "11px",
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                      margin: 0,
                                      fontFamily: "monospace",
                                      lineHeight: "1.6"
                                    }}>
                                      {emailData.stackTrace}
                                    </pre>
                                  </div>
                                )}

                                {/* Additional Information Section */}
                                {emailData.additionalInfo && (
                                  <div style={{ marginTop: "25px" }}>
                                    <h4 style={{ color: "#6c757d", marginBottom: "10px", fontSize: "14px", fontWeight: "600" }}>
                                      Additional Information
                                    </h4>
                                    <pre style={{
                                      background: "#f8f9fa",
                                      padding: "15px",
                                      borderRadius: "6px",
                                      overflowX: "auto",
                                      fontSize: "11px",
                                      border: "1px solid #e9ecef",
                                      margin: 0,
                                      fontFamily: "monospace",
                                      lineHeight: "1.6"
                                    }}>
                                      {emailData.additionalInfo}
                                    </pre>
                                  </div>
                                )}

                                {/* Email Footer */}
                                <div style={{
                                  marginTop: "25px",
                                  paddingTop: "15px",
                                  borderTop: "1px solid #e9ecef",
                                  textAlign: "center",
                                  fontSize: "12px",
                                  color: "#6c757d"
                                }}>
                                  <p style={{ margin: 0 }}>
                                    This is an automated error notification from RealEsy Application
                                  </p>
                                  <p style={{ margin: "5px 0 0 0" }}>
                                    Please investigate and resolve the issue promptly.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorLogViewer;

